"use client";

import * as React from "react";
import { ArrowRight, Layers, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice, ProductSortDropdown } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 6,
  orderby: "date",
  order: "desc"
};
const wp_query = new WP_Query(wp_query_params);
function PowderProductsList() {
  const {
    products,
    loading,
    error,
    refetch,
    totalPages,
    currentPage,
    nextPage,
    prevPage,
    hasNext,
    hasPrev
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-xs animate-pulse">
            <div className="aspect-4/3 w-full rounded-lg bg-muted" />
            <div className="h-5 w-3/4 rounded bg-muted" />
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-2/3 rounded bg-muted" />
            <div className="mt-auto pt-4 flex items-center justify-between">
              <div className="h-6 w-20 rounded bg-muted" />
              <div className="h-9 w-28 rounded bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-base text-muted-foreground">Unable to load powder testing solutions at this time.</p>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>;
  }
  if (products.length === 0) {
    return <div className="rounded-xl border border-border bg-card p-12 text-center">
        <Layers className="mx-auto size-10 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold text-foreground">No instruments found</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Check back shortly or contact our technical team for custom powder characterisation instruments.
        </p>
        <div className="mt-6">
          <Button asChild variant="default">
            <Link to="/ContactPage">Contact Technical Support</Link>
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <div key={product.id} data-index={i} className="flex">
            <Card className="flex w-full flex-col justify-between overflow-hidden border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
              <div>
                <Link to={product.link} className="block overflow-hidden bg-muted/40">
                  {product.featured_image_url ? <div className="aspect-4/3 w-full overflow-hidden">
                      <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" />
                    </div> : <div className="flex aspect-4/3 w-full items-center justify-center bg-secondary/50 text-muted-foreground">
                      <Layers className="size-12 opacity-40" />
                    </div>}
                </Link>

                <CardHeader className="gap-2 pt-5">
                  <div className="flex items-center justify-between gap-2">
                    {product.categories && product.categories.length > 0 ? <Badge variant="secondary" className="font-normal text-xs">
                        {product.categories[0].name}
                      </Badge> : <Badge variant="outline" className="font-normal text-xs">
                        Powder Testing
                      </Badge>}
                    {product.on_sale && <Badge variant="default" className="text-xs">
                        Special Offer
                      </Badge>}
                  </div>

                  <CardTitle className="text-lg font-semibold tracking-tight text-foreground line-clamp-2">
                    <Link to={product.link} className="hover:text-primary transition-colors">
                      {product.title}
                    </Link>
                  </CardTitle>

                  {product.short_description ? <div className="text-sm text-muted-foreground line-clamp-2 [&_p]:m-0" dangerouslySetInnerHTML={{
                __html: product.short_description
              }} /> : <CardDescription className="line-clamp-2">
                      Precision instrumentation and accessories engineered for regulated powder flow, volume, and tapping analysis.
                    </CardDescription>}
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="flex items-baseline gap-2">
                    <ProductPrice product={product} className="text-base font-semibold text-foreground" />
                  </div>
                </CardContent>
              </div>

              <CardFooter className="flex flex-col gap-3 border-t border-border/60 bg-muted/20 pt-4">
                <div className="w-full">
                  <AddToCartButton product={product} productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground shadow-xs hover:bg-primary/90" text="Request Order / Add to Cart" showQuantity={false} />
                </div>
                <Button asChild variant="ghost" size="sm" className="w-full justify-center text-xs font-medium text-muted-foreground hover:text-foreground">
                  <Link to={product.link}>
                    Technical Specifications
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>)}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-between border-t border-border pt-6">
          <p className="text-sm text-muted-foreground">
            Page <span className="font-medium text-foreground">{currentPage}</span> of{" "}
            <span className="font-medium text-foreground">{totalPages}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext}>
              Next
            </Button>
          </div>
        </div>}
    </div>;
}
export default function PowderSolutions() {
  return <section id="relatedpowdertestingsolutions" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-12">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-primary uppercase">
              <Sparkles className="size-3.5" />
              <span>Related Solutions</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Powder Testing & Flow Characterisation Systems
            </h2>
            <p className="text-base text-muted-foreground">
              Explore compliant instruments for density, flowability, permeability, and bulk volume analysis across regulated pharmaceutical and material manufacturing.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="outline" size="sm">
              <Link to="/PowderTabletTestingInstrumentsPage">View All Instruments</Link>
            </Button>
            <Button asChild variant="default" size="sm">
              <Link to="/ApplicationConsultationPage">Request Application Support</Link>
            </Button>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc"
      }}>
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-3 shadow-xs">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Layers className="size-4 text-primary" />
                <span>Showing analytical systems & certified accessories</span>
              </div>
              <div className="flex items-center gap-2">
                <ProductSortDropdown label="Sort instruments" />
              </div>
            </div>

            <PowderProductsList />
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}